import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { RHFTextField } from "../../components/Inputs/RHFTextField"

import * as styles from "./ContactForm.module.css"
import { usePost } from "../../hooks/usePost"
import { makeAbsoluteUrl } from "../../utility"
import { RHFCheckbox } from "../Inputs/RHFCheckbox"
import { Row, Col } from "../Grid"
import { Button } from "../Button"

export interface FormData {
  firstName: string
  lastName: string
  email: string
  mobileNumber: string
  companyName: string
  countryRegion: string
  terms: boolean
  promotion: boolean
}

const defaultValues: Partial<FormData> = {
  promotion: false,
  terms: false,
}

const getCheckBoxValue = (x: boolean) => x

export type ContactFormProps = {
  twoColumn?: boolean
  pageName: string
  advToken?: string
}

export const ContactForm = ({
  twoColumn,
  pageName,
  advToken,
}: ContactFormProps) => {
  const formMethods = useForm<FormData>({
    mode: "onChange",
    defaultValues,
  })

  const { handleSubmit, reset, setError, setValue } = formMethods

  const { loading, save, data } = usePost("Contact")
  const [submitted, setSubmitted] = useState(false)

  const onSubmit = (data: FormData) => {
    if (!getCheckBoxValue(data.terms)) {
      setError("terms", { message: "Terms and Conditions are required" })
      return
    }

    save({
      fields: [
        {
          name: "email",
          value: data.email,
        },
        {
          name: "firstname",
          value: data.firstName,
        },
        {
          name: "lastname",
          value: data.lastName,
        },
        {
          name: "company",
          value: data.companyName,
        },
        {
          name: "mobilephone",
          value: data.mobileNumber,
        },
        {
          name: "country",
          value: data.countryRegion,
        },
      ],
      context: {
        pageUri: makeAbsoluteUrl("contact"),
        pageName,
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text:
            "By submitting this form, you accept the site terms & conditions and the privacy policy.",
          communications: [
            {
              value: getCheckBoxValue(data.promotion),
              subscriptionTypeId: 999,
              text:
                "By checking this box, I agree to receive information and promotional material about Press'nXPress solutions.",
            },
          ],
        },
      },
    }).then(x => {
      setSubmitted(true)
      if (advToken) {
        // @ts-ignore
        window.gtag_report_conversion(advToken)
      }
    })
  }

  React.useEffect(() => {
    if (data && !loading) {
      reset()
      setValue("terms", false)
      setValue("promotion", false)
    }
  }, [data, loading])

  return !submitted ? (
    <FormProvider {...formMethods}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={twoColumn ? 0 : [24, 12]}>
          <Row
            style={{ width: "100%" }}
            gutter={twoColumn ? 12 : [24, 12]}
            justify="space-between"
          >
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="firstName"
                validation={{ required: true }}
                label="First name"
                className={styles.input}
              />
            </Col>
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="lastName"
                label="Last name"
                validation={{ required: true }}
                className={styles.input}
              />
            </Col>
          </Row>
          <Row
            gutter={twoColumn ? 12 : [24, 12]}
            style={{ width: "100%" }}
            justify="space-between"
          >
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="email"
                label="Email"
                type="email"
                validation={{
                  required: true,
                  pattern: {
                    message: "Email address must be valid.",
                    value: /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/,
                  },
                }}
                className={styles.input}
              />
            </Col>
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="mobileNumber"
                label="Mobile number"
                className={styles.input}
                validation={{
                  required: true,
                  pattern: {
                    value: /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)/,
                    message:
                      "Please enter a valid phone number e.g. +1##########",
                  },
                }}
              />
            </Col>
          </Row>
          <Row
            gutter={twoColumn ? 12 : [24, 12]}
            style={{ width: "100%" }}
            justify="space-between"
          >
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="companyName"
                label="Company name"
                className={styles.input}
                validation={{ required: true }}
              />
            </Col>
            <Col xs={24} md={twoColumn ? 12 : 24}>
              <RHFTextField
                name="countryRegion"
                label="Country/Region"
                className={styles.input}
              />
            </Col>
          </Row>
          <RHFCheckbox
            name="promotion"
            label="By checking this box, I agree to receive information and promotional material about Press'nXPress solutions. "
            className={styles.input}
          />
          <RHFCheckbox
            name="terms"
            label="By submitting this form, you accept the site terms & conditions and the privacy policy."
            className={styles.input}
            validation={{
              required: {
                message: "Terms and Conditions are required",
                value: true,
              },
            }}
          />

          <Button
            loading={loading}
            disabled={!formMethods.formState.isValid}
            className={styles.submit}
            onClick={handleSubmit(onSubmit)}
          >
            <>Submit</>
          </Button>
        </Row>
      </form>
    </FormProvider>
  ) : (
    <span style={{ fontWeight: 700 }}>
      Thanks for submitting your information. Someone from our team will contact
      you.
    </span>
  )
}

export default ContactForm
